import { Button, Col, Form, Row, Space, theme } from "antd";
import { useEffect, useState } from "react";
import Data from "../controls/Data";
import { DataRequest } from "../controls/Data.default";
import { QueryOperation, SearchQuery } from "../code/search-query/search-query";
import { getCompanies, getCompanyUsers } from "../repository/data.repository";
import {
  mapUsersViewQuery,
  usersViewColumns,
} from "./CompanyUsersView.default";
import { retry } from "../utils/http";
import { nameOf } from "../utils/typescript";
import { Company } from "../repository/data.default";
import DynamicForm from "../controls/DynamicForm";

// DUMMY DATA
export interface CompanyUsersViewProps {
  readonly trigger?: string;
  readonly onSearch?: (searchQuery: SearchQuery) => void;
  readonly onAction?: (key: string, record: any) => void;
}

function CompanyUsersView(props: CompanyUsersViewProps) {
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const values = Form.useWatch([], form);

  const [loading, setLoading] = useState(false);
  const [context, setContext] = useState<{
    readonly searchQuery: SearchQuery;
  }>({
    searchQuery: {
      conditions: [],
    },
  });
  const [valuesValid, setValuesValid] = useState(false);

  // Lifecycle

  useEffect(() => {
    setValuesValid(!form.getFieldsError().some(({ errors }) => errors.length));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  useEffect(() => {
    setContext({ ...context });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.trigger]);

  // Events

  function onLoading(loading: boolean): void {
    setLoading(loading);
  }

  function onSearchData(): void {
    const searchQuery = mapUsersViewQuery(values);
    setContext({
      searchQuery,
    });

    props.onSearch?.(searchQuery);
  }

  function onClearData(): void {
    setValuesValid(true);
    form.resetFields();
    setContext({
      searchQuery: {
        conditions: [],
      },
    });
  }

  function onAction(key: string, record: any): void {
    props.onAction?.(key, record);
  }

  // Methods

  const request = ({ context, pageNumber, pageSize, sort }: DataRequest) => {
    const sortBy = (sort || [])[0]?.key ?? null;
    const sortDescending = (sort || [])[0]?.direction === "desc";

    return getCompanyUsers({
      ...context,
      pageNumber: 0,
      pageSize: 100,
      ...(sortBy
        ? {
            sortBy,
            sortDescending,
          }
        : {}),
    }).then(({ data, totalRecords }) => ({
      data,
      pageNumber,
      pageSize,
      total: totalRecords,
    }));
  };

  // companiesRequest removed — see Audit fix 3 comment above.

  return (
    <Row className="flex flex-auto flex-row flex-nowrap overflow-hidden">
      {/* Form */}
      <Col
        className="mr-4 flex flex-col border border-solid pl-2 pr-2"
        style={{
          borderColor: token.colorBorder,
          borderRadius: token.borderRadius,
        }}
      >
        <Space className="flex flex-row justify-end pb-2 pt-2">
          <Button disabled={!valuesValid || loading} onClick={onClearData}>
            Clear
          </Button>
          <Button
            type="primary"
            loading={loading}
            disabled={!valuesValid}
            onClick={onSearchData}
          >
            Search
          </Button>
        </Space>
        <DynamicForm
          className="w-72 overflow-y-auto"
          form={form}
          schema={{
            groups: [
              {
                controls: [
                  // Audit fix 3 (2026-11-04) — the Company combobox used to call
                  // /api/Data/Companies which is admin-only; a company user
                  // got a silent 403 and the dropdown was always empty. The
                  // backend auto-scopes this list to the user's own company
                  // anyway, so the filter is dropped.
                  {
                    type: "textbox",
                    name: "userName",
                    label: "User Name",
                  },
                  {
                    type: "textbox",
                    name: "firstName",
                    label: "First Name",
                  },
                  {
                    type: "textbox",
                    name: "middleName",
                    label: "Middle Name",
                  },
                  {
                    type: "textbox",
                    name: "lastName",
                    label: "Last Name",
                  },
                  {
                    type: "datepicker",
                    name: "dateOfBirth",
                    label: "Date Of Birth",
                  },
                  {
                    type: "textbox",
                    name: "email",
                    label: "Email",
                  },
                  {
                    type: "togglebox",
                    name: "emailConfirmed",
                    label: "Email Confirmed",
                  },
                  {
                    type: "textbox",
                    name: "phoneNumber",
                    label: "Phone",
                  },
                  {
                    type: "textbox",
                    name: "companyPhoneNumber",
                    label: "Company Phone",
                  },
                ],
              },
            ],
          }}
        />
      </Col>
      {/* Data */}
      <Data
        columns={usersViewColumns}
        context={context}
        pageSize={100}
        request={request}
        rowKey={({ uid }) => uid}
        onLoading={onLoading}
        actions={[
          {
            key: "view",
            label: "View",
            handler: onAction,
          },
          {
            key: "delete",
            label: "Delete",
            danger: true,
            handler: onAction,
          },
        ]}
      />
    </Row>
  );
}

export default CompanyUsersView;
